/**
 * Feature E05-F05 — "Admin: команды (recruiter)" request/response contracts.
 *
 * 1:1 port of the legacy n8n `manage_teams` action router (group Teams,
 * docs/source-n8n/reference/Teams.md + sql-queries.md "Teams: Get All / Execute
 * Create / Execute Update / Execute Delete"). The old SPA POSTed
 * `{ command:'manage_teams', action, ...payload, session_token }` to one webhook;
 * the v2 transport is clean REST under `/admin/teams` but the SAME logical
 * contract — the four CRUD actions over the `teams` table (a recruiter team =
 * a `name`, an optional `teamlead_id` → users, and a derived `members` list).
 *
 * Endpoints honoured by the feature route (`routes/admin-teams.ts`):
 *   GET    /admin/teams        → { success:true, teams: TeamDTO[] }            (get_teams)
 *   POST   /admin/teams        → { success:true, message:'Team created' }      (create_team)
 *   PATCH  /admin/teams/:id    → { success:true, message:'Team updated' }      (update_team)
 *   DELETE /admin/teams/:id    → { success:true, message:'Team delete' }       (delete_team)
 *
 * KEY denormalization (Teams.md gotcha — preserved 1:1):
 *   - a team's MEMBERS are `users` whose `team_id === teams.NAME` (the team NAME
 *     string, NOT teams.id), excluding the service user id='system';
 *   - the TEAM LEAD is `users.id === teams.teamlead_id`.
 * So `members` here is DERIVED at read time (legacy correlated `json_agg`
 * subquery), it is NOT stored on the team row. Membership is assigned elsewhere
 * (editing a user's team_id) — this admin tab shows members read-only.
 *
 * Parity notes carried over from the legacy slice (Teams.md gotchas):
 *   - get_teams returns ONLY is_active=true, ORDER BY name; teamlead joined via
 *     teams.teamlead_id = users.id (teamlead_name / teamlead_email, null if none).
 *   - create requires name (legacy masked an empty name as success — v2 surfaces
 *     "Name is required"); teamlead_id is OPTIONAL (NULL when omitted).
 *   - update is a FULL replace of name + teamlead_id (PUT semantics).
 *   - delete is a HARD DELETE (`DELETE FROM teams`) — NOT a soft delete; this is
 *     the key behavioural difference from the sourcing_teams table, which is soft.
 *     The legacy success message keeps its typo "Team delete" (not "deleted").
 *
 * Hardening over the legacy slice (Teams.md gotchas about raw `WHERE id=${teamId}`
 * interpolation and manual quote-escaping): v2 uses Prisma parameterized writes
 * and STRICT integer validation of `:id` at the route boundary.
 *
 * IDOR note: the legacy slice read no role/user from the session and let any
 * authenticated user CRUD/delete any team. v2 still requires a valid session
 * (`app.authenticate`); the route mounts under the admin surface.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Service user id always excluded from a team's members (Teams.md). */
export const TEAM_SYSTEM_USER_ID = 'system';
/** Validation message for an empty name (legacy masked it; v2 is honest). */
export const TEAM_NAME_REQUIRED_MESSAGE = 'Name is required';
/** Static legacy success messages (verbatim — note the "Team delete" typo). */
export const TEAM_CREATED_MESSAGE = 'Team created';
export const TEAM_UPDATED_MESSAGE = 'Team updated';
export const TEAM_DELETED_MESSAGE = 'Team delete';
// ---------------------------------------------------------------------------
// Member (one row of the derived json_agg members list — Teams.md)
// ---------------------------------------------------------------------------
/**
 * One recruiter-team member, mirroring the legacy correlated subquery
 * `json_build_object('id',u2.id,'name',u2.name,'email',u2.email,'role',u2.role)`.
 */
export const teamMemberSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
});
// ---------------------------------------------------------------------------
// Read DTO — Teams: Get All projection
// ---------------------------------------------------------------------------
/**
 * One recruiter team, mirroring the legacy `Teams: Get All` SELECT exactly:
 * `id, name, teamlead_id, is_active, teamlead_name, teamlead_email, members`
 * (members = json_agg of users where users.team_id = teams.name and id != system).
 */
export const teamSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    teamlead_id: z.string().nullable(),
    /** Always true on the wire (get_teams filters is_active=true). */
    is_active: z.boolean(),
    /** Joined via teams.teamlead_id = users.id; null when no teamlead assigned. */
    teamlead_name: z.string().nullable(),
    teamlead_email: z.string().nullable(),
    /** Derived members (users.team_id = teams.NAME, excluding id='system'). */
    members: z.array(teamMemberSchema),
});
/** GET /admin/teams response — active teams, ORDER BY name. */
export const teamsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    teams: z.array(teamSchema),
});
// ---------------------------------------------------------------------------
// Create — Teams: Build/Execute Create
// ---------------------------------------------------------------------------
/**
 * POST /admin/teams body. `name` is REQUIRED (legacy masked empty as success;
 * v2 returns "Name is required"). `teamlead_id` is OPTIONAL — a users.id, or
 * null/omitted for no team lead.
 */
export const createTeamSchema = z.object({
    name: z.string().trim().min(1, TEAM_NAME_REQUIRED_MESSAGE),
    teamlead_id: z.string().trim().min(1).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Update — Teams: Build/Execute Update (full replace)
// ---------------------------------------------------------------------------
/**
 * PATCH /admin/teams/:id body. Legacy `update_team` fully replaced name +
 * teamlead_id; v2 keeps that — `name` required, `teamlead_id` optional/nullable
 * (omitted teamlead_id clears the lead, matching `teamlead_id || NULL`).
 */
export const updateTeamSchema = z.object({
    name: z.string().trim().min(1, TEAM_NAME_REQUIRED_MESSAGE),
    teamlead_id: z.string().trim().min(1).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Mutation responses (static legacy messages)
// ---------------------------------------------------------------------------
/** Create response — static legacy message "Team created". */
export const teamCreateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.literal(TEAM_CREATED_MESSAGE),
});
/** Update response — static legacy message "Team updated". */
export const teamUpdateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.literal(TEAM_UPDATED_MESSAGE),
});
/** Delete response — static legacy message (verbatim typo "Team delete"). */
export const teamDeleteResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.literal(TEAM_DELETED_MESSAGE),
});
//# sourceMappingURL=admin-teams.js.map